// Medpax logo system — mark variants + wordmark compositions.
// All marks are pure SVG, scalable, work in navy/white/any brand color.

// ──────────────────────────────────────────────────────────────────
// MARK 1 — "Seal M" (current): architectural serif M inside a square seal
// ──────────────────────────────────────────────────────────────────
export const MarkSealM = ({ size = 28, color = "currentColor", className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-hidden="true"
    >
        <rect x="2" y="2" width="44" height="44" rx="2" stroke={color} strokeWidth="1.5" />
        <path
            d="M12 36 L12 12 L24 26 L36 12 L36 36"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="miter"
            strokeLinecap="square"
            fill="none"
        />
        <line x1="9" y1="36" x2="15" y2="36" stroke={color} strokeWidth="1.5" strokeLinecap="square" />
        <line x1="33" y1="36" x2="39" y2="36" stroke={color} strokeWidth="1.5" strokeLinecap="square" />
        <line x1="24" y1="30" x2="24" y2="34" stroke={color} strokeWidth="1.5" strokeLinecap="square" />
    </svg>
);

// ──────────────────────────────────────────────────────────────────
// MARK 2 — "Circle Seal": serif M inside a thin circle with wordmark ticks
// Feels vintage / old-money / notarial
// ──────────────────────────────────────────────────────────────────
export const MarkCircleSeal = ({ size = 28, color = "currentColor", className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        className={className}
        aria-hidden="true"
    >
        <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="1.2" />
        <circle cx="24" cy="24" r="17" stroke={color} strokeWidth="0.6" />
        {/* serif M */}
        <path
            d="M14 32 L14 16 L24 26 L34 16 L34 32"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="miter"
            strokeLinecap="square"
            fill="none"
        />
        <line x1="12" y1="32" x2="16" y2="32" stroke={color} strokeWidth="1.5" />
        <line x1="32" y1="32" x2="36" y2="32" stroke={color} strokeWidth="1.5" />
        {/* four tiny dots at compass points */}
        <circle cx="24" cy="3.5" r="0.9" fill={color} />
        <circle cx="24" cy="44.5" r="0.9" fill={color} />
        <circle cx="3.5" cy="24" r="0.9" fill={color} />
        <circle cx="44.5" cy="24" r="0.9" fill={color} />
    </svg>
);

// ──────────────────────────────────────────────────────────────────
// MARK 3 — "Chevron M": negative-space M from two overlapping chevrons
// Modern / tech-studio feel
// ──────────────────────────────────────────────────────────────────
export const MarkChevronM = ({ size = 28, color = "currentColor", className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        className={className}
        aria-hidden="true"
    >
        <path
            d="M6 36 L16 12 L24 28 L32 12 L42 36"
            stroke={color}
            strokeWidth="3"
            strokeLinejoin="round"
            strokeLinecap="round"
            fill="none"
        />
        <path
            d="M14 36 L20 24"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
        />
        <path
            d="M34 36 L28 24"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
        />
    </svg>
);

// ──────────────────────────────────────────────────────────────────
// MARK 4 — "Monogram": refined serif M with swash, framed by thin lines
// Editorial / luxurious
// ──────────────────────────────────────────────────────────────────
export const MarkMonogram = ({ size = 28, color = "currentColor", className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        className={className}
        aria-hidden="true"
    >
        <line x1="4" y1="10" x2="44" y2="10" stroke={color} strokeWidth="0.8" />
        <line x1="4" y1="38" x2="44" y2="38" stroke={color} strokeWidth="0.8" />
        {/* Ornate serif-style M */}
        <path
            d="M10 34 L10 14 L14 14 L24 28 L34 14 L38 14 L38 34"
            stroke={color}
            strokeWidth="2.2"
            strokeLinejoin="miter"
            fill="none"
        />
        {/* serif feet */}
        <line x1="7" y1="34" x2="13" y2="34" stroke={color} strokeWidth="1.5" />
        <line x1="35" y1="34" x2="41" y2="34" stroke={color} strokeWidth="1.5" />
        {/* swash accent under M */}
        <path d="M20 36 Q24 38 28 36" stroke={color} strokeWidth="0.8" fill="none" strokeLinecap="round" />
    </svg>
);

// ──────────────────────────────────────────────────────────────────
// MARK 5 — "Pax Cross": minimal M where the middle valley becomes a subtle cross
// Nods to "pax" (peace). Clean, modern, subtle meaning.
// ──────────────────────────────────────────────────────────────────
export const MarkPaxCross = ({ size = 28, color = "currentColor", className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        className={className}
        aria-hidden="true"
    >
        <path
            d="M8 38 L8 10 L24 26 L40 10 L40 38"
            stroke={color}
            strokeWidth="2.4"
            strokeLinejoin="miter"
            strokeLinecap="square"
            fill="none"
        />
        {/* small cross inside the valley */}
        <line x1="24" y1="30" x2="24" y2="38" stroke={color} strokeWidth="2" strokeLinecap="square" />
        <line x1="20" y1="34" x2="28" y2="34" stroke={color} strokeWidth="1.6" strokeLinecap="square" />
    </svg>
);

// ──────────────────────────────────────────────────────────────────
// MARK 6 — "Pure wordmark" (no symbol) — Medpax in serif with a single dot
// Most minimal, very high-end
// ──────────────────────────────────────────────────────────────────
export const WordmarkDot = ({ color = "currentColor", className = "", size = 40 }) => (
    <span
        className={`inline-flex items-baseline gap-2 font-display ${className}`}
        style={{ color, fontSize: size, lineHeight: 1, letterSpacing: "-0.02em" }}
    >
        <span>Medpax</span>
        <span
            style={{
                width: size * 0.12,
                height: size * 0.12,
                borderRadius: "9999px",
                background: color,
                display: "inline-block",
                alignSelf: "center",
            }}
        />
    </span>
);

// ──────────────────────────────────────────────────────────────────
// Backwards-compatible aliases used elsewhere on the site
// ──────────────────────────────────────────────────────────────────
export const MedpaxMark = MarkCircleSeal;

export const MedpaxLogo = ({
    variant = "full",
    color = "currentColor",
    suffix = true,
    className = "",
    markSize = 28,
    mark: MarkComponent = MarkCircleSeal,
}) => {
    if (variant === "mark") {
        return <MarkComponent size={markSize} color={color} className={className} />;
    }
    return (
        <span
            className={`inline-flex items-center gap-2.5 ${className}`}
            style={{ color }}
            data-testid="medpax-logo"
        >
            <MarkComponent size={markSize} color={color} />
            <span className="flex flex-col leading-none">
                <span className="font-display text-2xl tracking-tight">
                    Medpax
                    {suffix && <span className="text-[var(--slate)]">, LLC</span>}
                </span>
                <span className="overline text-[var(--slate)] mt-1">
                    Software Studio
                </span>
            </span>
        </span>
    );
};

export default MedpaxLogo;
